const stretchFactoryPromise = { current: null };
const decodedAudio = new WeakMap();

const loadStretchFactory = () => {
  if (!stretchFactoryPromise.current) {
    stretchFactoryPromise.current = import(
      /* webpackIgnore: true */ "/vendor/signalsmith-stretch/SignalsmithStretch.mjs"
    ).then((module) => module.default);
  }
  return stretchFactoryPromise.current;
};

const decodeFile = async (context, file) => {
  let promise = decodedAudio.get(file);
  if (!promise) {
    promise = file.arrayBuffer().then((bytes) => context.decodeAudioData(bytes.slice(0)));
    decodedAudio.set(file, promise);
  }
  return promise;
};

export const createTempoStretchPreviewVoice = async ({
  context,
  destination,
  file,
  startFraction,
  endFraction,
  loopEnabled,
  loopStartFraction,
  loopEndFraction,
  tempoRate,
  semitones,
  pan,
}) => {
  const [createStretch, buffer] = await Promise.all([
    loadStretchFactory(),
    decodeFile(context, file),
  ]);
  // Use Signalsmith's default node layout. Its buffered-playback path expects
  // the default (unconnected) input bus; forcing zero inputs makes the worklet
  // throw on its first inactive render quantum and leaves the preview silent.
  const stretch = await createStretch(context);
  await stretch.configure({ blockMs: 80, intervalMs: 20, splitComputation: true });
  await stretch.addBuffers(Array.from({ length: buffer.numberOfChannels }, (_, channel) => (
    new Float32Array(buffer.getChannelData(channel))
  )));

  const gain = context.createGain();
  const stereoPanner = typeof context.createStereoPanner === "function" ? context.createStereoPanner() : null;
  gain.gain.value = 1;
  if (stereoPanner) {
    stereoPanner.pan.value = Math.max(-1, Math.min(1, Number(pan) || 0));
    stretch.connect(stereoPanner);
    stereoPanner.connect(gain);
  } else {
    stretch.connect(gain);
  }
  gain.connect(destination || context.destination);

  const start = buffer.duration * Math.max(0, Math.min(0.999, Number(startFraction) || 0));
  const end = buffer.duration * Math.max(0.001, Math.min(1, Number(endFraction) || 1));
  const loopStart = buffer.duration * Math.max(Number(startFraction) || 0, Math.min(Number(endFraction) || 1, Number(loopStartFraction) || 0));
  const loopEnd = buffer.duration * Math.max(Number(startFraction) || 0, Math.min(Number(endFraction) || 1, Number(loopEndFraction) || 1));
  let currentLoopEnabled = !!loopEnabled;
  let currentLoopStart = loopStart;
  let currentLoopEnd = loopEnd;
  let currentRate = Math.max(1 / 15, Math.min(15, Number(tempoRate) || 1));
  let currentSemitones = Number(semitones) || 0;
  let playing = false;
  let stopped = false;
  let volume = 1;

  const voice = {
    duration: buffer.duration,
    paused: true,
    ended: false,
    onloadedmetadata: null,
    onplay: null,
    onpause: null,
    onended: null,
    onerror: null,
    _vstTempoStretch: true,
    _vstAudioNodes: { source: stretch, stereoPanner, gain },
    get currentTime() { return Number(stretch.inputTime) || start; },
    set currentTime(value) {
      if (stopped) return;
      const input = Math.max(start, Math.min(end, Number(value) || start));
      stretch.schedule({ input, output: context.currentTime, rate: currentRate, semitones: currentSemitones }).catch(() => {});
    },
    get volume() { return volume; },
    set volume(value) {
      volume = Math.max(0, Math.min(1, Number(value) || 0));
      gain.gain.setTargetAtTime(volume, context.currentTime, 0.008);
    },
    get playbackRate() { return currentRate; },
    set playbackRate(value) { currentRate = Math.max(1 / 15, Math.min(15, Number(value) || 1)); },
    async play() {
      if (stopped) throw new Error("Tempo-stretch voice has already stopped");
      const latency = await stretch.latency();
      const output = context.currentTime + Math.max(0, Number(latency) || 0);
      await stretch.schedule({
        active: true,
        input: start,
        output,
        rate: currentRate,
        semitones: currentSemitones,
        loopStart: currentLoopEnabled && currentLoopEnd > currentLoopStart ? currentLoopStart : 0,
        loopEnd: currentLoopEnabled && currentLoopEnd > currentLoopStart ? currentLoopEnd : 0,
      });
      playing = true;
      voice.paused = false;
      voice.onplay?.();
    },
    pause() {
      if (stopped) return;
      stopped = true;
      playing = false;
      voice.paused = true;
      stretch.stop(context.currentTime).catch(() => {});
      voice.onpause?.();
    },
    _vstSetTempoRate(value) {
      currentRate = Math.max(1 / 15, Math.min(15, Number(value) || 1));
      if (playing) stretch.schedule({ output: context.currentTime, rate: currentRate, semitones: currentSemitones }).catch(() => {});
    },
    _vstSetExpressionSemitones(value) {
      currentSemitones = Number(value) || 0;
      if (playing) stretch.schedule({ output: context.currentTime, rate: currentRate, semitones: currentSemitones }).catch(() => {});
    },
    _vstSetLoop(enabled, nextStartFraction, nextEndFraction) {
      currentLoopEnabled = !!enabled;
      currentLoopStart = buffer.duration * Math.max(0, Math.min(1, Number(nextStartFraction) || 0));
      currentLoopEnd = buffer.duration * Math.max(0, Math.min(1, Number(nextEndFraction) || 1));
      if (playing) stretch.schedule({
        output: context.currentTime,
        loopStart: currentLoopEnabled && currentLoopEnd > currentLoopStart ? currentLoopStart : 0,
        loopEnd: currentLoopEnabled && currentLoopEnd > currentLoopStart ? currentLoopEnd : 0,
      }).catch(() => {});
    },
  };

  await stretch.setUpdateInterval(1 / 30, (inputTime) => {
    if (!playing || currentLoopEnabled || inputTime < end) return;
    playing = false;
    stopped = true;
    voice.paused = true;
    voice.ended = true;
    stretch.stop(context.currentTime).catch(() => {});
    voice.onended?.();
  });
  queueMicrotask(() => voice.onloadedmetadata?.());
  return voice;
};
